import { useState } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Menu, Save, Loader2, Eye, EyeOff, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { useQuery, useQueryClient } from 'react-query';
import { getAllExpertises, updateExpertise, reorderExpertises, type Expertise } from '@/api/expertiseService';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

export default function CmsExpertiseEditor() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Expertise>>({});
  const [saving, setSaving] = useState(false);

  const { data: expertises = [], isLoading } = useQuery('all-expertises', getAllExpertises);

  const handleSave = async (id: string) => {
    try {
      setSaving(true);
      await updateExpertise(id, { title: editData.title, subtitle: editData.subtitle, description: editData.description, imageUrl: editData.imageUrl, buttonText: editData.buttonText });
      queryClient.invalidateQueries('all-expertises');
      setEditingId(null);
      setEditData({});
      toast({ title: 'Opgeslagen' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Opslaan mislukt', description: String(err) });
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      await updateExpertise(id, { isActive: !currentActive });
      queryClient.invalidateQueries('all-expertises');
    } catch (err) {
      toast({ variant: 'destructive', title: 'Bijwerken mislukt', description: String(err) });
    }
  };

  const startEdit = (exp: Expertise) => {
    setEditingId(exp.id);
    setEditData({ title: exp.title, subtitle: exp.subtitle, description: exp.description, imageUrl: exp.imageUrl, buttonText: exp.buttonText });
  };

  const moveItem = async (index: number, direction: 'up' | 'down') => {
    const items = [...expertises];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
    await reorderExpertises(items.map((i) => i.id));
    queryClient.invalidateQueries('all-expertises');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-5xl mx-auto flex items-center gap-3">
              <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
              <div>
                <h1 className="text-xl font-semibold text-[#202223]">Expertises</h1>
                <p className="text-sm text-[#6d7175] mt-0.5">Beheer je diensten en specialisaties</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-5xl mx-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : (
                <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60 divide-y divide-gray-100">
                  {expertises.map((exp, index) => (
                    <div key={exp.id} className="flex items-start gap-4 px-5 py-4">
                      <div className="flex flex-col gap-0.5 pt-1 shrink-0">
                        <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="text-gray-300 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => moveItem(index, 'down')} disabled={index === expertises.length - 1} className="text-gray-300 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronDown className="h-3.5 w-3.5" /></button>
                      </div>

                      <img src={exp.imageUrl} alt={exp.title} className="w-12 h-12 object-cover rounded-lg shrink-0 bg-gray-100" />

                      {editingId === exp.id ? (
                        <div className="flex-1 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input value={editData.title || ''} onChange={(e) => setEditData({ ...editData, title: e.target.value })} placeholder="Titel" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-gray-300 focus:ring-0 outline-none" />
                            <input value={editData.subtitle || ''} onChange={(e) => setEditData({ ...editData, subtitle: e.target.value })} placeholder="Subtitel" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-gray-300 focus:ring-0 outline-none" />
                          </div>
                          <input value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} placeholder="Beschrijving" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-gray-300 focus:ring-0 outline-none" />
                          <div className="grid grid-cols-2 gap-2">
                            <input value={editData.imageUrl || ''} onChange={(e) => setEditData({ ...editData, imageUrl: e.target.value })} placeholder="Afbeelding URL" className="border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs focus:border-gray-300 focus:ring-0 outline-none" />
                            <input value={editData.buttonText || ''} onChange={(e) => setEditData({ ...editData, buttonText: e.target.value })} placeholder="Knoptekst" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-gray-300 focus:ring-0 outline-none" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => startEdit(exp)}>
                          <p className="text-sm font-medium text-[#202223]">{exp.title}</p>
                          <p className="text-[13px] text-[#6d7175] truncate">{exp.subtitle} &middot; {exp.description?.substring(0, 80)}...</p>
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                        {editingId === exp.id && (
                          <>
                            <button onClick={() => handleSave(exp.id)} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#202223] text-white px-3 py-1.5 rounded-lg text-[13px] font-medium hover:bg-[#333] disabled:opacity-50">
                              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                              Opslaan
                            </button>
                            <button onClick={() => setEditingId(null)} className="text-[13px] text-[#6d7175] hover:text-[#202223] px-2 py-1.5">Annuleer</button>
                          </>
                        )}
                        <button onClick={() => handleToggleActive(exp.id, exp.isActive)} className={`p-1.5 rounded-lg ${exp.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 hover:bg-gray-50'}`} title={exp.isActive ? 'Actief' : 'Inactief'}>
                          {exp.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button onClick={() => navigate(`/admin/cms/expertise-${exp.id}`)} className="p-1.5 rounded-lg text-gray-400 hover:text-[#202223] hover:bg-gray-50" title="Content bewerken">
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
